import { useMutation } from '@apollo/client';
import { FormProvider } from 'react-hook-form';
import { toast } from 'react-toastify';
import type { SoManagerWellLogPartsFragment } from '~/apollo/generated/v4/graphql';
import { Button } from '~/components/ui/button';
import { HookFormErrors } from '~/components/ui/forms/HookFormErrors';
import { SOFormFields } from '~/components/upload/supporting-object/so-form-fields';
import type { SOParent } from '~/components/upload/supporting-object/so-manager';
import { UPDATE_WELL_LOG } from '~/components/upload/supporting-object/so-operations';
import { useZodForm } from '~/utils/forms';
import {
  initialWellLog,
  wellLogSchema,
} from '~/utils/modules/supporting-object';

type Props = SOParent & {
  wellLog: SoManagerWellLogPartsFragment;
  onUpdateSuccess?: () => unknown;
};

export function WellLogUpdateForm({
  wellLog,
  studyId,
  onUpdateSuccess,
}: Props) {
  const [updateWellLog, { loading, error }] = useMutation(UPDATE_WELL_LOG);

  const form = useZodForm({
    schema: wellLogSchema,
    values: initialWellLog(wellLog),
  });

  const handleSubmit = form.handleSubmit(async values => {
    try {
      await updateWellLog({
        variables: {
          id: wellLog.id,
          input: values,
        },
      });
      toast.success('Well log updated successfully.');
      form.reset();
      if (onUpdateSuccess) onUpdateSuccess();
    } catch (err) {
      console.log('Error updating well log', err);
      toast.error('There was a problem updating the well log.');
    }
  });

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <SOFormFields soType="WELL_LOG" studyId={studyId} />
        <HookFormErrors graphQLError={error} />

        <div className="card-actions justify-end">
          <Button type="submit" loading={loading} color="primary">
            Save
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
